import React, { useEffect, useMemo, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";

const API_BASE = "https://doticom.vn:3000";

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("vi-VN", {
    style: "currency",
    currency: "VND",
    maximumFractionDigits: 0,
  });
const getToken = () => localStorage.getItem("token") || localStorage.getItem("tokens") || "";

const getCommonHeaders = ({ hasBody = false } = {}) => {
  const token = getToken();
  return {
    ...(hasBody ? { "Content-Type": "application/json" } : {}),
    ...(token ? { token } : {}),
  };
};

const mapStatusToMessage = (status) => {
  if (status === 400) return "Thiếu/sai tham số (StartDay và EndDay phải đi cùng nhau)";
  if (status === 401) return "Sai hoặc thiếu token";
  if (status === 404) return "Không tìm thấy dữ liệu";
  if (status === 500) return "Lỗi server/SQL";
  return "Có lỗi khi gọi API";
};

const normalizeDate = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);
  return "";
};

const toTimestamp = (value) => {
  if (!value) return Number.NEGATIVE_INFINITY;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : Number.NEGATIVE_INFINITY;
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return Object.values(value).filter((row) => row && typeof row === "object");
  return [];
};

const normalizeDetails = (value) => {
  const rows = toArray(value);
  if (rows.length > 0) return rows;
  return value && typeof value === "object" ? [value] : [];
};

const getTransactionGroups = (item) => {
  const groupsFromData = toArray(item?.Data);
  const looksLikeGroupedData = groupsFromData.some(
    (row) => row && typeof row === "object" && (row?.NgayXuat || row?.Data !== undefined)
  );

  if (looksLikeGroupedData) return groupsFromData;

  const groupsFromAltFields = toArray(item?.TransactionsByDate || item?.Rows || item?.Items);
  return groupsFromAltFields;
};

const getTransactionDetailsFromItem = (item) => {
  const groups = getTransactionGroups(item);
  if (groups.length > 0) {
    return groups.flatMap((group) => normalizeDetails(group?.Data));
  }
  return normalizeDetails(item?.Data);
};

const getCashInRecordCount = (item) => {
  const explicitCount = Number(item?.totalRecords);
  if (Number.isFinite(explicitCount) && explicitCount > 0) return explicitCount;
  return getTransactionDetailsFromItem(item).length || (item?.TongTien ? 1 : 0);
};

const getCashInTotal = (item) => {
  const explicitTotal = Number(item?.totalCashIn);
  if (Number.isFinite(explicitTotal)) return Math.abs(explicitTotal);

  const details = getTransactionDetailsFromItem(item);
  if (details.length > 0) {
    return details.reduce((sum, row) => sum + Math.abs(Number(row?.TongTien || 0)), 0);
  }

  return Math.abs(Number(item?.TongTien || 0));
};

const readRowsFromResponse = (result) => {
  if (Array.isArray(result?.data)) return result.data;
  if (Array.isArray(result)) return result;
  return [];
};

const readStoresFromResponse = (result) => {
  if (Array.isArray(result)) return result;
  if (Array.isArray(result?.data)) return result.data;
  return [];
};

const normalizeMaCH = (value) => {
  const text = String(value ?? "").trim();
  return text || "";
};

const normalizeBankAccount = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return "";

  const lowerText = text.toLowerCase();
  if (lowerText === "null" || lowerText === "undefined" || lowerText === "n/a" || text === "-" || text === "0") {
    return "";
  }

  return text;
};

const pickBankAccount = (...values) => {
  for (const value of values) {
    const normalized = normalizeBankAccount(value);
    if (normalized) return normalized;
  }
  return "";
};

const buildAccountLabel = ({ NganHang, STKNganHang, MaCH }) => {
  const bankName = String(NganHang || "").trim();
  const bankAccount = normalizeBankAccount(STKNganHang);

  if (bankName && bankAccount) return `${bankName} - ${bankAccount}`;
  if (bankAccount) return bankAccount;
  return `MaCH ${MaCH ?? "Không rõ"}`;
};

const buildAccountKey = ({ MaCH, STKNganHang }) => {
  const maCH = normalizeMaCH(MaCH);
  if (maCH) return `MACH:${maCH}`;

  const bankAccount = normalizeBankAccount(STKNganHang);
  if (bankAccount) return `STK:${bankAccount}`;

  return "UNKNOWN";
};

const readBankInAccountInfo = (item) => {
  const data = item?.Data || {};
  return {
    MaCH: item?.MaCH,
    NganHang: item?.NganHang,
    STKNganHang: pickBankAccount(
      item?.STKNganHang,
      data?.STKNganHang,
      data?.SoTaiKhoan,
      data?.STK,
      item?.SoTaiKhoan,
      item?.STK
    ),
  };
};

const readCashOutAccountInfo = (item) => ({
  MaCH: item?.MaCH,
  NganHang: item?.NganHang,
  STKNganHang: pickBankAccount(item?.STKNganHang, item?.SoTaiKhoan, item?.STK),
});

const readCashBeginAccountInfo = (item) => ({
  MaCH: item?.MaCH,
  NganHang: item?.NganHang,
  STKNganHang: pickBankAccount(item?.STKNganHang, item?.SoTaiKhoan, item?.STK),
});

const readBankInLabel = (item) => {
  const info = readBankInAccountInfo(item);
  return buildAccountLabel(info);
};

const readCashOutLabel = (item) =>
  buildAccountLabel(readCashOutAccountInfo(item));

const readCashBeginLabel = (item) =>
  buildAccountLabel(readCashBeginAccountInfo(item));

const readCashInAmount = (item) => {
  return getCashInTotal(item);
};

const emptyCashOutForm = {
  IDChi: null,
  MaCH: "",
  SoTienChi: "",
  NoiDungChi: "",
  NgayChi: "",
  NganHang: "",
  STKNganHang: "",
  SoChungTu: "",
  NguoiChi: "",
  GhiChu: "",
};

const emptyCashBeginForm = {
  MaCH: "",
  Ngay: "",
  SoTienDauKy: "",
  NguoiNhap: "",
  ThoiDiemNhap: "",
  NganHang: "",
  STKNganHang: "",
};

function ModalShell({ title, onClose, children, zIndex = 1055 }) {
  return (
    <>
      <div className="modal fade show" style={{ display: "block", zIndex }} tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-xl modal-dialog-scrollable" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{title}</h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
            </div>
            <div className="modal-body">{children}</div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" style={{ zIndex: zIndex - 1 }} onClick={onClose} />
    </>
  );
}

export default function SoNganHang() {
  const [startDay, setStartDay] = useState("2026-03-01");
  const [endDay, setEndDay] = useState("2026-03-03");
  const [selectedStoreIds, setSelectedStoreIds] = useState([]);
  const [storeDropdownOpen, setStoreDropdownOpen] = useState(false);
  const [storeSearch, setStoreSearch] = useState("");
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState("tong-hop");
  const [stores, setStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [responseMeta, setResponseMeta] = useState({
    page: 1,
    pageSize: 0,
    totalRecords: 0,
    totalPages: 0,
    totalCashIn: 0,
  });

  const [transactions, setTransactions] = useState([]);
  const [cashOuts, setCashOuts] = useState([]);
  const [cashBegins, setCashBegins] = useState([]);

  const [showCashOutInputModal, setShowCashOutInputModal] = useState(false);
  const [showCashOutManageModal, setShowCashOutManageModal] = useState(false);
  const [showCashBeginManageModal, setShowCashBeginManageModal] = useState(false);

  const [cashOutForm, setCashOutForm] = useState(emptyCashOutForm);
  const [cashBeginForm, setCashBeginForm] = useState(emptyCashBeginForm);
  const [cashBeginEditKey, setCashBeginEditKey] = useState(null);

  const requestJson = async (method, path, body) => {
    const hasBody = body !== undefined && body !== null;
    const response = await fetch(`${API_BASE}${path}`, {
      method,
      headers: getCommonHeaders({ hasBody }),
      ...(hasBody ? { body: JSON.stringify(body) } : {}),
    });

    const text = await response.text();
    let data;
    try {
      data = text ? JSON.parse(text) : null;
    } catch {
      data = { message: text };
    }

    if (!response.ok) {
      throw new Error(data?.message || mapStatusToMessage(response.status));
    }

    if (data && typeof data === "object" && data.success === false) {
      throw new Error(data?.message || "API trả về không thành công");
    }

    return data;
  };

  const apiGet = (path) => requestJson("GET", path);
  const apiPost = (path, body) => requestJson("POST", path, body);
  const apiPut = (path, body) => requestJson("PUT", path, body);
  const apiDelete = (path) => requestJson("DELETE", path);

  useEffect(() => {
    const fetchStores = async () => {
      try {
        setLoadingStores(true);
        const result = await apiGet("/stores");
        const rows = readStoresFromResponse(result)
          .filter((store) => store && store.IDCH !== undefined && store.IDCH !== null)
          .sort((a, b) => Number(a?.IDCH || 0) - Number(b?.IDCH || 0));
        setStores(rows);
      } catch {
        setStores([]);
      } finally {
        setLoadingStores(false);
      }
    };

    fetchStores();
  }, []);

  const buildFilterParams = (targetPage) => {
    const params = new URLSearchParams({ page: String(targetPage || 1) });

    const hasStart = Boolean(startDay);
    const hasEnd = Boolean(endDay);

    if (hasStart !== hasEnd) {
      throw new Error("StartDay và EndDay phải truyền cùng nhau");
    }

    if (hasStart && hasEnd) {
      params.append("StartDay", startDay);
      params.append("EndDay", endDay);
    }

    const storeParam = selectedStoreIds
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .join(",");

    if (storeParam) {
      params.append("MaCH", storeParam);
    }

    return params;
  };

  const fetchBankTransactions = async (targetPage) => {
    const params = buildFilterParams(targetPage);
    const result = await apiGet(`/api/bank-transactions?${params.toString()}`);

    const rows = Array.isArray(result?.data) ? result.data : [];
    const calculatedTotalCashIn = rows.reduce((sum, row) => sum + getCashInTotal(row), 0);
    const calculatedTotalRecords = rows.reduce((sum, row) => sum + getCashInRecordCount(row), 0);

    setTransactions(rows);
    setResponseMeta({
      page: Number(result?.page || targetPage || 1),
      pageSize: Number(result?.pageSize || 0),
      totalRecords: Number(result?.totalRecords || calculatedTotalRecords || 0),
      totalPages: Number(result?.totalPages || 0),
      totalCashIn: Number(result?.totalCashIn || calculatedTotalCashIn || 0),
    });
    setPage(Number(result?.page || targetPage || 1));
  };

  const fetchCashOuts = async (targetPage = 1) => {
    const params = buildFilterParams(targetPage);
    params.append("includeDeleted", "false");

    const result = await apiGet(`/api/cash-outs?${params.toString()}`);
    setCashOuts(readRowsFromResponse(result));
  };

  const fetchCashBegins = async (targetPage = 1) => {
    const params = new URLSearchParams({ page: String(targetPage || 1) });
    const storeParam = selectedStoreIds
      .map((value) => String(value || "").trim())
      .filter(Boolean)
      .join(",");
    if (storeParam) {
      params.append("MaCH", storeParam);
    }
    const result = await apiGet(`/api/cash-begins?${params.toString()}`);
    setCashBegins(readRowsFromResponse(result));
  };

  const fetchAllData = async (targetPage = page) => {
    setLoading(true);
    setError("");

    try {
      await Promise.all([fetchBankTransactions(targetPage), fetchCashOuts(targetPage), fetchCashBegins(targetPage)]);
    } catch (err) {
      setError(`Không tải được dữ liệu: ${err.message}`);
      setTransactions([]);
      setCashOuts([]);
      setCashBegins([]);
      setResponseMeta({
        page: targetPage,
        pageSize: 0,
        totalRecords: 0,
        totalPages: 0,
        totalCashIn: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  const selectedStoresSummary = useMemo(() => {
    if (selectedStoreIds.length === 0) return "Tất cả cửa hàng";

    const selectedNames = stores
      .filter((store) => selectedStoreIds.includes(String(store?.IDCH)))
      .map((store) => {
        const code = String(store?.IDCH);
        const name = String(store?.TenCuaHang || store?.TenCuaHang_Uni || "").trim();
        return name ? `${code} - ${name}` : code;
      });

    if (selectedNames.length === 1) return selectedNames[0];
    return `Đã chọn ${selectedNames.length} cửa hàng`;
  }, [selectedStoreIds, stores]);

  const filteredStores = useMemo(() => {
    const keyword = storeSearch.trim().toLowerCase();
    if (!keyword) return stores;

    return stores.filter((store) => {
      const code = String(store?.IDCH || "").toLowerCase();
      const name = String(store?.TenCuaHang || store?.TenCuaHang_Uni || "").toLowerCase();
      return code.includes(keyword) || name.includes(keyword);
    });
  }, [stores, storeSearch]);

  const toggleStoreSelection = (storeId) => {
    setSelectedStoreIds((prev) => {
      if (prev.includes(storeId)) {
        return prev.filter((value) => value !== storeId);
      }
      return [...prev, storeId];
    });
  };

  const groupedByAccount = useMemo(() => {
    const accountMap = new Map();

    const ensureRow = (accountInfo) => {
      const accountKey = buildAccountKey(accountInfo);
      const accountLabel = buildAccountLabel(accountInfo);

      if (!accountMap.has(accountKey)) {
        accountMap.set(accountKey, {
          accountKey,
          accountLabel,
          MaCH: normalizeMaCH(accountInfo?.MaCH),
          NganHang: String(accountInfo?.NganHang || "").trim(),
          STKNganHang: normalizeBankAccount(accountInfo?.STKNganHang),
          soGiaoDichThu: 0,
          soPhieuChi: 0,
          tienDauKy: 0,
          tongThu: 0,
          tongChi: 0,
          conLai: 0,
        });
      }

      const row = accountMap.get(accountKey);
      const incomingStk = normalizeBankAccount(accountInfo?.STKNganHang);
      const incomingBank = String(accountInfo?.NganHang || "").trim();

      if (!row.STKNganHang && incomingStk) row.STKNganHang = incomingStk;
      if (!row.NganHang && incomingBank) row.NganHang = incomingBank;
      if (!row.MaCH && normalizeMaCH(accountInfo?.MaCH)) row.MaCH = normalizeMaCH(accountInfo?.MaCH);

      row.accountLabel = buildAccountLabel({
        MaCH: row.MaCH,
        NganHang: row.NganHang,
        STKNganHang: row.STKNganHang,
      });

      return row;
    };

    transactions.forEach((item) => {
      const row = ensureRow(readBankInAccountInfo(item));
      row.soGiaoDichThu += getCashInRecordCount(item);
      row.tongThu += getCashInTotal(item);
      row.conLai = row.tienDauKy + row.tongThu - row.tongChi;
    });

    cashOuts.forEach((item) => {
      const row = ensureRow(readCashOutAccountInfo(item));
      row.soPhieuChi += 1;
      row.tongChi += Math.abs(Number(item?.SoTienChi || 0));
      row.conLai = row.tienDauKy + row.tongThu - row.tongChi;
    });

    const latestCashBeginByAccountKey = new Map();
    cashBegins.forEach((item) => {
      const accountInfo = readCashBeginAccountInfo(item);
      const accountKey = buildAccountKey(accountInfo);
      const itemTime = toTimestamp(item?.Ngay);
      const existing = latestCashBeginByAccountKey.get(accountKey);
      const existingTime = existing ? toTimestamp(existing?.Ngay) : Number.NEGATIVE_INFINITY;

      if (!existing || itemTime >= existingTime) {
        latestCashBeginByAccountKey.set(accountKey, item);
      }
    });

    latestCashBeginByAccountKey.forEach((item) => {
      const row = ensureRow(readCashBeginAccountInfo(item));
      row.tienDauKy = Math.abs(Number(item?.SoTienDauKy || 0));
      row.conLai = row.tienDauKy + row.tongThu - row.tongChi;
    });

    return [...accountMap.values()].sort((a, b) => b.conLai - a.conLai);
  }, [transactions, cashOuts, cashBegins]);

  const totals = useMemo(
    () =>
      groupedByAccount.reduce(
        (acc, row) => {
          acc.tienDauKy += row.tienDauKy;
          acc.tongThu += row.tongThu;
          acc.tongChi += row.tongChi;
          acc.conLai += row.conLai;
          return acc;
        },
        { tienDauKy: 0, tongThu: 0, tongChi: 0, conLai: 0 }
      ),
    [groupedByAccount]
  );

  const pageOptions = useMemo(
    () => Array.from({ length: Math.max(1, responseMeta.totalPages || 1) }, (_, index) => index + 1),
    [responseMeta.totalPages]
  );

  const incomeTransactionRows = useMemo(() => {
    return transactions.flatMap((item) => {
      const groups = getTransactionGroups(item);

      if (groups.length > 0) {
        return groups.flatMap((group) => {
          const details = normalizeDetails(group?.Data);
          if (details.length === 0) {
            return [
              {
                MaCH: item?.MaCH,
                NgayXuat: normalizeDate(group?.NgayXuat || item?.NgayXuat),
                NganHang: item?.NganHang || group?.NganHang,
                STKNganHang: item?.STKNganHang || group?.STKNganHang,
                IDXuat: "",
                TongTien: Number(group?.TongTien || 0),
                GiamPT: 0,
                GiamTM: 0,
                MaHTTT: "",
                Act_T: "",
              },
            ];
          }

          return details.map((detail) => ({
            MaCH: item?.MaCH,
            NgayXuat: normalizeDate(group?.NgayXuat || item?.NgayXuat),
            NganHang: item?.NganHang || group?.NganHang,
            STKNganHang: item?.STKNganHang || group?.STKNganHang,
            IDXuat: detail?.IDXuat,
            TongTien: Number(detail?.TongTien || 0),
            GiamPT: Number(detail?.GiamPT || 0),
            GiamTM: Number(detail?.GiamTM || 0),
            MaHTTT: detail?.MaHTTT,
            Act_T: detail?.Act_T,
          }));
        });
      }

      const details = normalizeDetails(item?.Data);
      if (details.length === 0) {
        return [
          {
            MaCH: item?.MaCH,
            NgayXuat: normalizeDate(item?.NgayXuat),
            NganHang: item?.NganHang,
            STKNganHang: item?.STKNganHang,
            IDXuat: "",
            TongTien: Number(item?.TongTien || 0),
            GiamPT: 0,
            GiamTM: 0,
            MaHTTT: "",
            Act_T: "",
          },
        ];
      }

      return details.map((detail) => ({
        MaCH: item?.MaCH,
        NgayXuat: normalizeDate(item?.NgayXuat),
        NganHang: item?.NganHang,
        STKNganHang: item?.STKNganHang,
        IDXuat: detail?.IDXuat,
        TongTien: Number(detail?.TongTien || 0),
        GiamPT: Number(detail?.GiamPT || 0),
        GiamTM: Number(detail?.GiamTM || 0),
        MaHTTT: detail?.MaHTTT,
        Act_T: detail?.Act_T,
      }));
    });
  }, [transactions]);

  const resetCashOutForm = () => setCashOutForm(emptyCashOutForm);

  const handleSubmitCashOut = async () => {
    if (!cashOutForm.MaCH || !cashOutForm.SoTienChi || !cashOutForm.NoiDungChi || !cashOutForm.NgayChi) {
      setError("Phiếu chi cần: MaCH, SoTienChi, NoiDungChi, NgayChi");
      return;
    }

    const payload = {
      MaCH: Number(cashOutForm.MaCH),
      SoTienChi: Number(cashOutForm.SoTienChi),
      NoiDungChi: cashOutForm.NoiDungChi,
      NgayChi: cashOutForm.NgayChi,
      NganHang: cashOutForm.NganHang || undefined,
      STKNganHang: cashOutForm.STKNganHang || undefined,
      SoChungTu: cashOutForm.SoChungTu || undefined,
      NguoiChi: cashOutForm.NguoiChi || undefined,
      GhiChu: cashOutForm.GhiChu || undefined,
    };

    if (payload.SoTienChi < 0) {
      setError("SoTienChi phải >= 0");
      return;
    }

    try {
      setLoading(true);
      setError("");

      if (cashOutForm.IDChi) {
        await apiPut(`/api/cash-outs/${cashOutForm.IDChi}`, payload);
      } else {
        await apiPost(`/api/cash-outs`, payload);
      }

      setShowCashOutInputModal(false);
      resetCashOutForm();
      await fetchAllData(page);
    } catch (err) {
      setError(`Lưu phiếu chi thất bại: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCashOut = async (id) => {
    if (!id) return;
    if (!window.confirm("Xóa mềm phiếu chi này?")) return;

    try {
      setLoading(true);
      setError("");
      await apiDelete(`/api/cash-outs/${id}`);
      await fetchAllData(page);
    } catch (err) {
      setError(`Xóa phiếu chi thất bại: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCashOut = (row) => {
    setCashOutForm({
      IDChi: row?.IDChi || row?.id || null,
      MaCH: String(row?.MaCH ?? ""),
      SoTienChi: String(row?.SoTienChi ?? ""),
      NoiDungChi: row?.NoiDungChi || "",
      NgayChi: normalizeDate(row?.NgayChi),
      NganHang: row?.NganHang || "",
      STKNganHang: row?.STKNganHang || "",
      SoChungTu: row?.SoChungTu || "",
      NguoiChi: row?.NguoiChi || "",
      GhiChu: row?.GhiChu || "",
    });
    setShowCashOutInputModal(true);
  };

  const resetCashBeginForm = () => {
    setCashBeginForm(emptyCashBeginForm);
    setCashBeginEditKey(null);
  };

  const handleSubmitCashBegin = async () => {
    if (!cashBeginForm.MaCH || !cashBeginForm.Ngay || !cashBeginForm.SoTienDauKy || !cashBeginForm.NguoiNhap) {
      setError("Tiền đầu kỳ cần: MaCH, Ngay, SoTienDauKy, NguoiNhap");
      return;
    }

    const payload = {
      MaCH: Number(cashBeginForm.MaCH),
      Ngay: cashBeginForm.Ngay,
      SoTienDauKy: Number(cashBeginForm.SoTienDauKy),
      NguoiNhap: cashBeginForm.NguoiNhap,
      ThoiDiemNhap: cashBeginForm.ThoiDiemNhap || undefined,
      NganHang: cashBeginForm.NganHang || undefined,
      STKNganHang: cashBeginForm.STKNganHang || undefined,
    };

    if (payload.SoTienDauKy < 0) {
      setError("SoTienDauKy phải >= 0");
      return;
    }

    try {
      setLoading(true);
      setError("");

      if (cashBeginEditKey) {
        await apiPut(`/api/cash-begins/${cashBeginEditKey.MaCH}/${cashBeginEditKey.Ngay}`, payload);
      } else {
        await apiPost(`/api/cash-begins`, payload);
      }

      resetCashBeginForm();
      await fetchAllData(page);
    } catch (err) {
      setError(`Lưu tiền đầu kỳ thất bại: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCashBegin = (row) => {
    setCashBeginEditKey({ MaCH: row.MaCH, Ngay: normalizeDate(row.Ngay) });
    setCashBeginForm({
      MaCH: String(row?.MaCH ?? ""),
      Ngay: normalizeDate(row?.Ngay),
      SoTienDauKy: String(row?.SoTienDauKy ?? ""),
      NguoiNhap: row?.NguoiNhap || "",
      ThoiDiemNhap: row?.ThoiDiemNhap || "",
      NganHang: row?.NganHang || "",
      STKNganHang: row?.STKNganHang || "",
    });
  };

  const handleDeleteCashBegin = async (row) => {
    if (!window.confirm("Xóa tiền đầu kỳ này?")) return;

    try {
      setLoading(true);
      setError("");
      await apiDelete(`/api/cash-begins/${row.MaCH}/${normalizeDate(row.Ngay)}`);
      await fetchAllData(page);
    } catch (err) {
      setError(`Xóa tiền đầu kỳ thất bại: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid py-3">
      <div className="card shadow-sm border-0">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <h4 className="mb-0">Thống kê thu chi theo STK ngân hàng</h4>
            <span className="badge bg-primary">Tổng thu API: {formatCurrency(responseMeta.totalCashIn)}</span>
          </div>

          {error && <div className="alert alert-danger py-2">{error}</div>}

          <div className="row g-3 align-items-end mb-3">
            <div className="col-12 col-md-3">
              <label className="form-label">Từ ngày</label>
              <input className="form-control" type="date" value={startDay} onChange={(e) => setStartDay(e.target.value)} />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">Đến ngày</label>
              <input className="form-control" type="date" value={endDay} onChange={(e) => setEndDay(e.target.value)} />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">Cửa hàng</label>
              <div
                className="dropdown"
                tabIndex={0}
                onBlur={(e) => {
                  if (!e.currentTarget.contains(e.relatedTarget)) {
                    setStoreDropdownOpen(false);
                  }
                }}
              >
                <button
                  type="button"
                  className="btn btn-outline-secondary dropdown-toggle w-100 text-start"
                  onClick={() => setStoreDropdownOpen((prev) => !prev)}
                  disabled={loadingStores}
                >
                  {selectedStoresSummary}
                </button>

                {storeDropdownOpen && (
                  <div className="dropdown-menu show w-100 p-2" style={{ maxHeight: 240, overflowY: "auto", border: "1px solid #89c0f8" }}>
                    <div className="mb-2">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Tìm theo mã hoặc tên..."
                        value={storeSearch}
                        onChange={(e) => setStoreSearch(e.target.value)}
                      />
                    </div>

                    {stores.length === 0 ? (
                      <div className="text-muted small px-2 py-1">Không có cửa hàng</div>
                    ) : filteredStores.length === 0 ? (
                      <div className="text-muted small px-2 py-1">Không tìm thấy cửa hàng phù hợp</div>
                    ) : (
                      filteredStores.map((store) => {
                        const code = String(store?.IDCH);
                        const name = String(store?.TenCuaHang || store?.TenCuaHang_Uni || "").trim();
                        const label = name ? `${code} - ${name}` : code;
                        const checked = selectedStoreIds.includes(code);

                        return (
                          <label key={code} className="dropdown-item d-flex align-items-center gap-2 mb-0">
                            <input
                              type="checkbox"
                              className="form-check-input mt-0"
                              checked={checked}
                              onChange={() => toggleStoreSelection(code)}
                            />
                            <span>{label}</span>
                          </label>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="col-12 col-md-3 d-grid">
              <button className="btn btn-primary" type="button" onClick={() => fetchAllData(page)} disabled={loading}>
                {loading ? "Đang tải..." : "Lấy dữ liệu"}
              </button>
            </div>
          </div>

          <div className="d-flex flex-wrap gap-2 mb-3">
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={() => fetchAllData(Math.max(1, page - 1))}
              disabled={loading || page <= 1}
            >
              Trang trước
            </button>

            <div className="input-group" style={{ width: 180 }}>
              <span className="input-group-text">Trang</span>
              <select
                className="form-select"
                value={page}
                onChange={(e) => {
                  const selected = Number(e.target.value);
                  setPage(selected);
                  fetchAllData(selected);
                }}
                disabled={loading}
              >
                {pageOptions.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>

            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={() => fetchAllData(page + 1)}
              disabled={loading || (responseMeta.totalPages > 0 && page >= responseMeta.totalPages)}
            >
              Trang sau
            </button>

            <button
              className="btn btn-outline-primary"
              type="button"
              onClick={() => {
                resetCashOutForm();
                setShowCashOutInputModal(true);
              }}
            >
              Nhập phiếu chi
            </button>
            <button className="btn btn-outline-primary" type="button" onClick={() => setShowCashOutManageModal(true)}>
              Quản lý phiếu chi
            </button>
            <button className="btn btn-outline-primary" type="button" onClick={() => setShowCashBeginManageModal(true)}>
              Quản lý tiền đầu kỳ
            </button>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-12 col-md-3">
              <div className="card border-start border-4 border-secondary h-100">
                <div className="card-body py-2">
                  <div className="small text-muted">Tiền đầu kỳ</div>
                  <div className="fw-semibold">{formatCurrency(totals.tienDauKy)}</div>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-3">
              <div className="card border-start border-4 border-success h-100">
                <div className="card-body py-2">
                  <div className="small text-muted">Tổng thu (gom dòng)</div>
                  <div className="fw-semibold text-success">{formatCurrency(totals.tongThu)}</div>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-3">
              <div className="card border-start border-4 border-danger h-100">
                <div className="card-body py-2">
                  <div className="small text-muted">Tổng chi</div>
                  <div className="fw-semibold text-danger">{formatCurrency(totals.tongChi)}</div>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-3">
              <div className={`card border-start border-4 ${totals.conLai >= 0 ? "border-success" : "border-danger"} h-100`}>
                <div className="card-body py-2">
                  <div className="small text-muted">Còn lại</div>
                  <div className={`fw-semibold ${totals.conLai >= 0 ? "text-success" : "text-danger"}`}>
                    {formatCurrency(totals.conLai)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <ul className="nav nav-tabs mb-3 flex-nowrap overflow-auto" style={{ whiteSpace: "nowrap" }}>
            <li className="nav-item" style={{width: "fit-content"}}>
              <button
                type="button"
                className={`nav-link ${activeTab === "tong-hop" ? "active" : ""}`}
                onClick={() => setActiveTab("tong-hop")}
              >
                Tổng hợp thu/ chi
              </button>
            </li>
            <li className="nav-item" style={{width: "fit-content"}}>
              <button
                type="button"
                className={`nav-link ${activeTab === "giao-dich-thu" ? "active" : ""}`}
                onClick={() => setActiveTab("giao-dich-thu")}
              >
                DS giao dịch thu
              </button>
            </li>
            <li className="nav-item" style={{width: "fit-content"}}>
              <button
                type="button"
                className={`nav-link ${activeTab === "giao-dich-chi" ? "active" : ""}`}
                onClick={() => setActiveTab("giao-dich-chi")}
              >
                DS giao dịch chi
              </button>
            </li>
          </ul>

          {activeTab === "tong-hop" && (
            <div className="table-responsive">
              <table className="table table-striped table-bordered align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Tài khoản / MaCH</th>
                    <th className="text-end">Số GD thu</th>
                    <th className="text-end">Số phiếu chi</th>
                    <th className="text-end">Tiền đầu kỳ</th>
                    <th className="text-end">Tổng thu</th>
                    <th className="text-end">Tổng chi</th>
                    <th className="text-end">Còn lại</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedByAccount.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-muted">
                        Chưa có dữ liệu.
                      </td>
                    </tr>
                  ) : (
                    groupedByAccount.map((row) => (
                      <tr key={row.accountLabel}>
                        <td>{row.accountLabel}</td>
                        <td className="text-end">{row.soGiaoDichThu}</td>
                        <td className="text-end">{row.soPhieuChi}</td>
                        <td className="text-end">{formatCurrency(row.tienDauKy)}</td>
                        <td className="text-end text-success fw-semibold">{formatCurrency(row.tongThu)}</td>
                        <td className="text-end text-danger fw-semibold">{formatCurrency(row.tongChi)}</td>
                        <td className={`text-end fw-semibold ${row.conLai >= 0 ? "text-success" : "text-danger"}`}>
                          {formatCurrency(row.conLai)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === "giao-dich-thu" && (
            <div className="table-responsive">
              <table className="table table-striped table-bordered align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Ngày xuất</th>
                    <th>MaCH</th>
                    <th>Ngân hàng</th>
                    <th>STK</th>
                    <th>IDXuat</th>
                    <th className="text-end">Tổng tiền</th>
                    <th className="text-end">Giảm PT</th>
                    <th className="text-end">Giảm TM</th>
                    <th>MaHTTT</th>
                    <th>Act_T</th>
                  </tr>
                </thead>
                <tbody>
                  {incomeTransactionRows.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="text-center py-4 text-muted">
                        Không có giao dịch thu.
                      </td>
                    </tr>
                  ) : (
                    incomeTransactionRows.map((row, index) => (
                      <tr key={`${row.IDXuat || "idx"}-${index}`}>
                        <td>{index + 1}</td>
                        <td>{row.NgayXuat || "-"}</td>
                        <td>{row.MaCH ?? "-"}</td>
                        <td>{row.NganHang || "-"}</td>
                        <td>{row.STKNganHang || "-"}</td>
                        <td>{row.IDXuat || "-"}</td>
                        <td className="text-end text-success fw-semibold">{formatCurrency(row.TongTien)}</td>
                        <td className="text-end">{formatCurrency(row.GiamPT)}</td>
                        <td className="text-end">{formatCurrency(row.GiamTM)}</td>
                        <td>{row.MaHTTT ?? "-"}</td>
                        <td>{row.Act_T ?? "-"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === "giao-dich-chi" && (
            <div className="table-responsive">
              <table className="table table-striped table-bordered align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Ngày chi</th>
                    <th>MaCH</th>
                    <th>Ngân hàng</th>
                    <th>STK</th>
                    <th>Nội dung chi</th>
                    <th>Người chi</th>
                    <th>Số chứng từ</th>
                    <th className="text-end">Số tiền chi</th>
                  </tr>
                </thead>
                <tbody>
                  {cashOuts.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center py-4 text-muted">
                        Không có giao dịch chi.
                      </td>
                    </tr>
                  ) : (
                    cashOuts.map((row, index) => (
                      <tr key={`${row.IDChi || "chi"}-${index}`}>
                        <td>{index + 1}</td>
                        <td>{normalizeDate(row.NgayChi)}</td>
                        <td>{row.MaCH ?? "-"}</td>
                        <td>{row.NganHang || "-"}</td>
                        <td>{row.STKNganHang || "-"}</td>
                        <td>{row.NoiDungChi || "-"}</td>
                        <td>{row.NguoiChi || "-"}</td>
                        <td>{row.SoChungTu || "-"}</td>
                        <td className="text-end text-danger fw-semibold">{formatCurrency(row.SoTienChi)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showCashOutInputModal && (
        <ModalShell
          title={cashOutForm.IDChi ? "Cập nhật phiếu chi" : "Nhập phiếu chi"}
          onClose={() => setShowCashOutInputModal(false)}
          zIndex={1080}
        >
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label">MaCH *</label>
              <input
                className="form-control"
                type="number"
                value={cashOutForm.MaCH}
                onChange={(e) => setCashOutForm((prev) => ({ ...prev, MaCH: e.target.value }))}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Số tiền chi *</label>
              <input
                className="form-control"
                type="number"
                min={0}
                value={cashOutForm.SoTienChi}
                onChange={(e) => setCashOutForm((prev) => ({ ...prev, SoTienChi: e.target.value }))}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Ngày chi *</label>
              <input
                className="form-control"
                type="date"
                value={cashOutForm.NgayChi}
                onChange={(e) => setCashOutForm((prev) => ({ ...prev, NgayChi: e.target.value }))}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Nội dung chi *</label>
              <input
                className="form-control"
                type="text"
                value={cashOutForm.NoiDungChi}
                onChange={(e) => setCashOutForm((prev) => ({ ...prev, NoiDungChi: e.target.value }))}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Ngân hàng</label>
              <input
                className="form-control"
                type="text"
                value={cashOutForm.NganHang}
                onChange={(e) => setCashOutForm((prev) => ({ ...prev, NganHang: e.target.value }))}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">STK ngân hàng</label>
              <input
                className="form-control"
                type="text"
                value={cashOutForm.STKNganHang}
                onChange={(e) => setCashOutForm((prev) => ({ ...prev, STKNganHang: e.target.value }))}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Số chứng từ</label>
              <input
                className="form-control"
                type="text"
                value={cashOutForm.SoChungTu}
                onChange={(e) => setCashOutForm((prev) => ({ ...prev, SoChungTu: e.target.value }))}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Người chi</label>
              <input
                className="form-control"
                type="text"
                value={cashOutForm.NguoiChi}
                onChange={(e) => setCashOutForm((prev) => ({ ...prev, NguoiChi: e.target.value }))}
              />
            </div>
            <div className="col-md-12">
              <label className="form-label">Ghi chú</label>
              <textarea
                className="form-control"
                rows={3}
                value={cashOutForm.GhiChu}
                onChange={(e) => setCashOutForm((prev) => ({ ...prev, GhiChu: e.target.value }))}
              />
            </div>
          </div>

          <div className="d-flex justify-content-end mt-3">
            <button type="button" className="btn btn-primary" onClick={handleSubmitCashOut} disabled={loading}>
              {cashOutForm.IDChi ? "Cập nhật" : "Tạo phiếu chi"}
            </button>
          </div>
        </ModalShell>
      )}

      {showCashOutManageModal && (
        <ModalShell title="Quản lý phiếu chi" onClose={() => setShowCashOutManageModal(false)}>
          <div className="table-responsive">
            <table className="table table-sm table-bordered table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>ID</th>
                  <th>MaCH</th>
                  <th>Ngày chi</th>
                  <th>Nội dung</th>
                  <th className="text-end">Số tiền</th>
                  <th>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {cashOuts.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-3">
                      Không có phiếu chi.
                    </td>
                  </tr>
                ) : (
                  cashOuts.map((row) => {
                    const id = row?.IDChi || row?.id;
                    return (
                      <tr key={id || `${row.MaCH}-${row.NgayChi}-${row.SoTienChi}`}>
                        <td>{id || "-"}</td>
                        <td>{row.MaCH}</td>
                        <td>{normalizeDate(row.NgayChi)}</td>
                        <td>{row.NoiDungChi}</td>
                        <td className="text-end text-danger fw-semibold">{formatCurrency(row.SoTienChi)}</td>
                        <td>
                          <div className="btn-group btn-group-sm">
                            <button type="button" className="btn btn-outline-primary" onClick={() => handleEditCashOut(row)}>
                              Sửa
                            </button>
                            <button type="button" className="btn btn-outline-danger" onClick={() => handleDeleteCashOut(id)}>
                              Xóa
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </ModalShell>
      )}

      {showCashBeginManageModal && (
        <ModalShell title="Quản lý tiền đầu kỳ" onClose={() => setShowCashBeginManageModal(false)}>
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">MaCH *</label>
              <input
                className="form-control"
                type="number"
                value={cashBeginForm.MaCH}
                onChange={(e) => setCashBeginForm((prev) => ({ ...prev, MaCH: e.target.value }))}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Ngày *</label>
              <input
                className="form-control"
                type="date"
                value={cashBeginForm.Ngay}
                onChange={(e) => setCashBeginForm((prev) => ({ ...prev, Ngay: e.target.value }))}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Số tiền đầu kỳ *</label>
              <input
                className="form-control"
                type="number"
                min={0}
                value={cashBeginForm.SoTienDauKy}
                onChange={(e) => setCashBeginForm((prev) => ({ ...prev, SoTienDauKy: e.target.value }))}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Người nhập *</label>
              <input
                className="form-control"
                type="text"
                value={cashBeginForm.NguoiNhap}
                onChange={(e) => setCashBeginForm((prev) => ({ ...prev, NguoiNhap: e.target.value }))}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Thời điểm nhập</label>
              <input
                className="form-control"
                type="text"
                value={cashBeginForm.ThoiDiemNhap}
                onChange={(e) => setCashBeginForm((prev) => ({ ...prev, ThoiDiemNhap: e.target.value }))}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Ngân hàng</label>
              <input
                className="form-control"
                type="text"
                value={cashBeginForm.NganHang}
                onChange={(e) => setCashBeginForm((prev) => ({ ...prev, NganHang: e.target.value }))}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">STK ngân hàng</label>
              <input
                className="form-control"
                type="text"
                value={cashBeginForm.STKNganHang}
                onChange={(e) => setCashBeginForm((prev) => ({ ...prev, STKNganHang: e.target.value }))}
              />
            </div>
          </div>

          <div className="d-flex gap-2 justify-content-end mt-3">
            {cashBeginEditKey && (
              <button type="button" className="btn btn-outline-secondary" onClick={resetCashBeginForm}>
                Hủy sửa
              </button>
            )}
            <button type="button" className="btn btn-primary" onClick={handleSubmitCashBegin} disabled={loading}>
              {cashBeginEditKey ? "Cập nhật tiền đầu kỳ" : "Tạo tiền đầu kỳ"}
            </button>
          </div>

          <div className="table-responsive mt-3">
            <table className="table table-sm table-bordered table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>MaCH</th>
                  <th>Ngày</th>
                  <th className="text-end">Số tiền đầu kỳ</th>
                  <th>Người nhập</th>
                  <th>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {cashBegins.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-3">
                      Không có bản ghi tiền đầu kỳ.
                    </td>
                  </tr>
                ) : (
                  cashBegins.map((row) => (
                    <tr key={`${row.MaCH}-${normalizeDate(row.Ngay)}`}>
                      <td>{row.MaCH}</td>
                      <td>{normalizeDate(row.Ngay)}</td>
                      <td className="text-end fw-semibold">{formatCurrency(row.SoTienDauKy)}</td>
                      <td>{row.NguoiNhap}</td>
                      <td>
                        <div className="btn-group btn-group-sm">
                          <button type="button" className="btn btn-outline-primary" onClick={() => handleEditCashBegin(row)}>
                            Sửa
                          </button>
                          <button type="button" className="btn btn-outline-danger" onClick={() => handleDeleteCashBegin(row)}>
                            Xóa
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </ModalShell>
      )}
    </div>
  );
}
